import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getInvoices,
  generateManualInvoice,
  anularInvoice,
  getTiposComprobante,
  getCondicionesIVA,
  getInvoiceById,
} from '../features/invoice/invoiceSlice';
import { getFiscalStatus } from '../features/fiscal/fiscalSlice';
import Loading from '../components/Loading';
import httpRequest from '../utils/request';
import { toast } from 'react-toastify';
import { useModalEscape } from '../utils/useModalEscape';
import { pageBg, cardStyle as cardUnified } from '../styles/ui';

const Invoices = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { invoices, tiposComprobante, condicionesIVA, loading } = useSelector((state) => state.invoice);
  const { fiscalStatus } = useSelector((state) => state.fiscal);
  const { tenant: tenantData } = useSelector((state) => state.tenant);

  const [showModal, setShowModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filter, setFilter] = useState('all'); // all | facturas | anuladas | nc
  const [sourceFilter, setSourceFilter] = useState('local'); // local | ecommerce | all
  const [query, setQuery] = useState('');

  // ESC key handler for modal
  useModalEscape(showModal, () => setShowModal(false));

  // Formulario
  const [formData, setFormData] = useState({
    puntoVenta: '',
    tipoComprobante: 6, // Factura B por defecto
    cliente: {
      tipoDocumento: 99,
      numeroDocumento: '0',
      razonSocial: 'Consumidor Final',
      condicionIVA: 5
    },
    items: [
      {
        descripcion: '',
        cantidad: 1,
        precioUnitario: 0,
        alicuotaIVA: 5 // 21%
      }
    ]
  });

  useEffect(() => {
    dispatch(getInvoices());
    dispatch(getTiposComprobante());
    dispatch(getCondicionesIVA());
    dispatch(getFiscalStatus());
  }, [dispatch]);

  useEffect(() => {
    // Configurar primer punto de venta disponible
    if (fiscalStatus?.puntosVenta && fiscalStatus.puntosVenta.length > 0) {
      setFormData(prev => ({
        ...prev,
        puntoVenta: fiscalStatus.puntosVenta[0].numero
      }));
    }
  }, [fiscalStatus]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('cliente.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        cliente: {
          ...prev.cliente,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        descripcion: '',
        cantidad: 1,
        precioUnitario: 0,
        alicuotaIVA: 5
      }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(generateManualInvoice(formData)).then(() => {
      setShowModal(false);
      // La acción ya inserta la factura en el slice (`invoice.invoices`), no es necesario re-fetch
      // Reset form
      setFormData({
        puntoVenta: fiscalStatus?.puntosVenta?.[0]?.numero || '',
        tipoComprobante: 6,
        cliente: {
          tipoDocumento: 99,
          numeroDocumento: '0',
          razonSocial: 'Consumidor Final',
          condicionIVA: 5
        },
        items: [{
          descripcion: '',
          cantidad: 1,
          precioUnitario: 0,
          alicuotaIVA: 5
        }]
      });
    });
  };

  const handleAnular = (invoiceId) => {
    if (window.confirm('¿Está seguro que desea anular esta factura?')) {
      dispatch(anularInvoice(invoiceId)).then((action) => {
        if (action && action.meta && action.meta.requestStatus === 'fulfilled') {
          const payload = action.payload || {};
          // Si el backend devolvió la venta actualizada, emitir evento para que SalesHistory la actualice
          if (payload.sale) {
            try {
              window.dispatchEvent(new CustomEvent('sale:annulled', { detail: payload.sale }));
            } catch (e) { /* ignore */ }
          }
        }
        // El slice `invoice` ya fue actualizado por la acción `anularInvoice.fulfilled`
      });
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
  };

  const handleViewNC = async (invoice) => {
    if (!invoice?.notaCreditoId) return;
    const found = invoices.find(i => i._id === invoice.notaCreditoId);
    if (found) {
      setSelectedInvoice(found);
      return;
    }
    const action = await dispatch(getInvoiceById(invoice.notaCreditoId));
    if (action.meta?.requestStatus === 'fulfilled') {
      setSelectedInvoice(action.payload);
    }
  };

  const handleViewOriginalFromNC = async (ncInvoice) => {
    const originalId = ncInvoice?.notaCreditoDe;
    if (!originalId) return;
    const found = invoices.find(i => i._id === originalId);
    if (found) {
      setSelectedInvoice(found);
      return;
    }
    const action = await dispatch(getInvoiceById(originalId));
    if (action.meta?.requestStatus === 'fulfilled') {
      setSelectedInvoice(action.payload);
    }
  };

  // Abrir PDF en una nueva pestaña (A4 o ticket80) trayéndolo como blob con auth headers
  const handleOpenPdf = async (invoice, format = 'a4') => {
    try {
      if (!invoice?._id) return;
      const resp = await httpRequest.get(`/invoices/${invoice._id}/pdf`, {
        params: { inline: true, format: format === 'ticket80' ? 'ticket80' : undefined },
        responseType: 'blob'
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });

      // Modo debug: calcular SHA-256 del PDF recibido y mostrarlo en consola/toast
      try {
        const dbgFlag = (typeof window !== 'undefined') && (new URLSearchParams(window.location.search).get('printDebug') === '1' || localStorage.getItem('printDebug') === '1');
        if (dbgFlag && typeof window.crypto !== 'undefined' && window.crypto.subtle) {
          try {
            let ab;
            try {
              // resp.data es Blob cuando axios responseType='blob'
              ab = await resp.data.arrayBuffer();
            } catch (convErr) {
              try { ab = await new Response(resp.data).arrayBuffer(); } catch (rErr) { ab = null; }
            }
            if (ab) {
              const hashBuf = await window.crypto.subtle.digest('SHA-256', ab);
              const hashArr = Array.from(new Uint8Array(hashBuf));
              const hashHex = hashArr.map((b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
              console.log('Invoice PDF SHA256 (debug):', hashHex);
              try { toast.info(`🔎 Invoice PDF SHA256: ${hashHex.slice(0, 16)}...`); } catch (t) { }
            }
          } catch (hErr) {
            console.warn('Could not compute invoice PDF SHA256:', hErr);
          }
        }
      } catch (e) {
        // ignore debug/hash errors
      }

      const url = window.URL.createObjectURL(blob);
      // Nombre amistoso para descarga si el navegador no embebe
      const suggested = `${[3, 8, 13, 53].includes(Number(invoice.tipoComprobante)) ? 'nota_credito' : 'factura'}_${String(invoice.puntoVenta).padStart(5, '0')}_${String(invoice.numero).padStart(8, '0')}_${format}.pdf`;
      // Intentar abrir en nueva pestaña
      const win = window.open(url, '_blank');
      if (!win) {
        // Si el popup fue bloqueado, forzar descarga
        const a = document.createElement('a');
        a.href = url;
        a.download = suggested;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Liberar recurso luego de un tiempo
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      console.error('No se pudo abrir el PDF', e);
      alert('No se pudo abrir el PDF. Intente nuevamente.');
    }
  };

  // Estilos
  const containerStyle = {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px'
  };

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2d3748'
  };

  const buttonStyle = (variant = 'primary') => ({
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s',
    background: variant === 'primary'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : variant === 'danger'
        ? '#e53e3e'
        : '#e2e8f0',
    color: variant === 'primary' || variant === 'danger' ? '#ffffff' : '#2d3748'
  });


  const cardStyle = { ...cardUnified, marginBottom: '20px' };

  const tabContainerStyle = { display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #e2e8f0' };
  const tabStyle = (active) => ({
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    borderBottom: active ? '3px solid #667eea' : '3px solid transparent',
    color: active ? '#2d3748' : '#718096',
    transition: 'all 0.2s'
  });

  const filterBarStyle = {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '10px'
  };

  const filterBtnStyle = (active) => ({
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '999px',
    border: '1px solid ' + (active ? '#667eea' : '#e2e8f0'),
    background: active ? 'rgba(102,126,234,0.12)' : '#fff',
    color: active ? '#2d3748' : '#4a5568',
    cursor: 'pointer'
  });

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '20px',
    tableLayout: 'fixed'
  };

  const thStyle = {
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #e2e8f0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#2d3748',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };

  const tdStyle = {
    padding: '12px',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#4a5568',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };

  const actionsContainerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: '44px'
  };

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    overflowY: 'auto',
    padding: '20px'
  };

  const modalContentStyle = {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568'
  };

  const formGroupStyle = {
    marginBottom: '15px'
  };

  const statusBadgeStyle = (estado) => ({
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    background: estado === 'autorizada' ? '#c6f6d5'
      : estado === 'anulada' ? '#fed7d7'
        : '#fef5e7',
    color: estado === 'autorizada' ? '#22543d'
      : estado === 'anulada' ? '#742a2a'
        : '#856404'
  });

  const alertStyle = {
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '20px',
    background: '#fef5e7',
    border: '1px solid #f39c12',
    color: '#856404',
    fontSize: '14px'
  };

  // Filtrado común para tabla y cards móviles
  const filteredInvoices = (invoices || [])
    .filter(inv => {
      // Guard clause: ignore null/undefined items to prevent crash
      if (!inv) return false;
      // Filtro por Origen (Source) explícito en la factura
      if (sourceFilter === 'ecommerce') return inv.source !== 'local' && inv.source;
      if (sourceFilter === 'local') return inv.source === 'local' || !inv.source;
      return true;
    })
    .filter(inv => {
      const isNC = [3, 8, 13, 53].includes(Number(inv.tipoComprobante));
      if (filter === 'all') return true;
      if (filter === 'facturas') return !isNC;
      if (filter === 'anuladas') return inv.estado === 'anulada';
      if (filter === 'nc') return isNC;
      return true;
    })
    .filter(inv => {
      if (!query) return true;
      const q = query.toLowerCase().trim();
      const razon = (inv.cliente?.razonSocial || '').toLowerCase();
      const cae = String(inv.cae || '').toLowerCase();
      const pv = String(inv.puntoVenta).padStart(5, '0');
      const num = String(inv.numero).padStart(8, '0');
      const comp = `${pv}-${num}`.toLowerCase();
      return razon.includes(q) || cae.includes(q) || comp.includes(q);
    });

  if (loading && invoices.length === 0) {
    return <Loading />;
  }

  if (!fiscalStatus?.isConfigured) {
    return (
      <div style={containerStyle}>
        <div style={alertStyle}>
          ⚠️ Debe configurar sus datos fiscales antes de generar facturas.
          <br />
          <a href="/fiscal-config" style={{ color: '#667eea', fontWeight: '600' }}>
            Ir a Configuración Fiscal →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={pageBg}>
      <div className="container-safe" style={containerStyle}>
        <div style={headerStyle}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '5px' }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  background: '#718096',
                  color: '#ffffff'
                }}
              >
                ← Volver
              </button>
              <h1 style={titleStyle}>🧾 Facturas Electrónicas</h1>
            </div>
            <p style={{ fontSize: '14px', color: '#718096', marginTop: '5px' }}>
              Gestión de facturas AFIP
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{ padding: '8px 12px', fontSize: '14px', fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#718096', color: '#ffffff' }}
            >
              🏠 Dashboard
            </button>
            <button
              onClick={() => navigate('/sales-history')}
              style={{ padding: '8px 12px', fontSize: '14px', fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#17a2b8', color: '#ffffff' }}
            >
              📜 Historial
            </button>
            <button
              onClick={() => navigate('/sales')}
              style={{ padding: '8px 12px', fontSize: '14px', fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#007bff', color: '#ffffff' }}
            >
              💵 Punto de Venta
            </button>
            <button
              onClick={() => navigate('/products')}
              style={{ padding: '8px 12px', fontSize: '14px', fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#28a745', color: '#ffffff' }}
            >
              📦 Productos
            </button>
          </div>
        </div>



        {/* TABS DE ORIGEN */}
        <div style={tabContainerStyle}>
          <button style={tabStyle(sourceFilter === 'local')} onClick={() => setSourceFilter('local')}>
            🏫 Ventas Local
          </button>
          {tenantData?.modules?.ecommerce?.enabled && tenantData?.modules?.ecommerce?.proEnabled !== false && (
            <button style={tabStyle(sourceFilter === 'ecommerce')} onClick={() => setSourceFilter('ecommerce')}>
              🛒 Ventas Ecommerce
            </button>
          )}
          <button style={tabStyle(sourceFilter === 'all')} onClick={() => setSourceFilter('all')}>
            📂 Todas
          </button>
        </div>

        {/* Listado de facturas */}
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>
            {sourceFilter === 'ecommerce' ? '🛒 Facturas de Ecommerce (Mercado Libre)' : '📋 Facturas Emitidas'}
          </h3>

          {/* Filtros + Buscador */}
          <div style={{ ...filterBarStyle, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={filterBtnStyle(filter === 'all')} onClick={() => setFilter('all')}>Todos</button>
              <button style={filterBtnStyle(filter === 'facturas')} onClick={() => setFilter('facturas')}>Facturas</button>
              <button style={filterBtnStyle(filter === 'anuladas')} onClick={() => setFilter('anuladas')}>Anuladas</button>
              <button style={filterBtnStyle(filter === 'nc')} onClick={() => setFilter('nc')}>Notas de Crédito</button>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 280 }}>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por cliente, PV-Número o CAE"
                style={{
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: 13,
                  width: '100%'
                }}
              />
              {query && (
                <button onClick={() => setQuery('')} style={filterBtnStyle(true)}>Limpiar</button>
              )}
            </div>
          </div>

          {invoices.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#718096', padding: '40px' }}>
              No hay facturas generadas aún
            </p>
          ) : (
            <>
              {/* Vista móvil: tarjetas */}
              <div className="show-on-mobile" style={{ display: 'grid', gap: 12 }}>
                {filteredInvoices.map((invoice) => (
                  <div key={invoice._id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ fontWeight: 700 }}>
                        {String(invoice.puntoVenta).padStart(5, '0')}-{String(invoice.numero).padStart(8, '0')}
                      </div>
                      <span style={statusBadgeStyle(invoice.estado)}>
                        {invoice.estado === 'autorizada' ? 'Autorizada' : invoice.estado === 'anulada' ? 'Anulada' : 'Pendiente'}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#555' }}>
                      <div><strong>Tipo:</strong> {tiposComprobante[invoice.tipoComprobante] || invoice.tipoComprobante}</div>
                      <div><strong>Cliente:</strong> {invoice.cliente?.razonSocial || '—'}</div>
                      <div><strong>Fecha:</strong> {new Date(invoice.fechaComprobante || invoice.fecha).toLocaleDateString('es-AR')}</div>
                      <div><strong>Total:</strong> ${invoice.total?.toFixed(2)}</div>
                      <div><strong>CAE:</strong> {invoice.cae || 'N/A'}</div>
                      <div>
                        <strong>NC:</strong>{' '}
                        {([3, 8, 13, 53].includes(Number(invoice.tipoComprobante)) && invoice.notaCreditoDe) ? (
                          (() => {
                            const original = invoices.find(i => i._id === invoice.notaCreditoDe);
                            return original ? `De ${String(original.puntoVenta).padStart(5, '0')}-${String(original.numero).padStart(8, '0')}` : 'De —';
                          })()
                        ) : (
                          invoice.notaCreditoId ? (() => {
                            const nc = invoices.find(i => i._id === invoice.notaCreditoId);
                            return nc ? `NC ${String(nc.puntoVenta).padStart(5, '0')}-${String(nc.numero).padStart(8, '0')}` : 'NC —';
                          })() : '—'
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                      <button onClick={() => handleViewInvoice(invoice)} style={{ ...buttonStyle('secondary'), padding: '8px 12px' }}>👁️ Ver</button>
                      {invoice.pdfUrl && (
                        <a href={`${invoice.pdfUrl}`} target="_blank" rel="noopener noreferrer" style={{ ...buttonStyle('primary'), padding: '8px 12px', textDecoration: 'none' }}>📄 PDF</a>
                      )}
                      {invoice.estado === 'autorizada' && ![3, 8, 13, 53].includes(Number(invoice.tipoComprobante)) && (
                        <button onClick={() => handleAnular(invoice._id)} style={{ ...buttonStyle('danger'), padding: '8px 12px' }}>❌ Anular</button>
                      )}
                      {invoice.estado === 'anulada' && invoice.notaCreditoId && (
                        <button onClick={() => handleViewNC(invoice)} style={{ ...buttonStyle('primary'), padding: '8px 12px' }}>🔎 Ver NC</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Vista escritorio: tabla */}
              <div className="responsive-table hide-on-mobile">
                <table style={tableStyle}>
                  <colgroup>
                    <col style={{ width: '140px' }} /> {/* Comprobante */}
                    <col style={{ width: '150px' }} /> {/* Tipo */}
                    <col /> {/* Cliente (auto) */}
                    <col style={{ width: '110px' }} /> {/* Fecha */}
                    <col style={{ width: '120px' }} /> {/* Total */}
                    <col style={{ width: '160px' }} /> {/* CAE */}
                    <col style={{ width: '160px' }} /> {/* NC */}
                    <col style={{ width: '120px' }} /> {/* Estado */}
                    <col style={{ width: '320px' }} /> {/* Acciones */}
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={thStyle}>Comprobante</th>
                      <th style={thStyle}>Tipo</th>
                      <th style={thStyle}>Cliente</th>
                      <th style={thStyle}>Fecha</th>
                      <th style={thStyle}>Total</th>
                      <th style={thStyle}>CAE</th>
                      <th style={thStyle}>NC</th>
                      <th style={{ ...thStyle, width: '10%' }}>Estado</th>
                      <th style={{ ...thStyle, width: '15%' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice._id}>
                        <td style={tdStyle} title={`${String(invoice.puntoVenta).padStart(5, '0')}-${String(invoice.numero).padStart(8, '0')}`}>
                          {String(invoice.puntoVenta).padStart(5, '0')}-
                          {String(invoice.numero).padStart(8, '0')}
                        </td>
                        <td style={tdStyle} title={tiposComprobante[invoice.tipoComprobante] || String(invoice.tipoComprobante)}>
                          {tiposComprobante[invoice.tipoComprobante] || invoice.tipoComprobante}
                        </td>
                        <td style={tdStyle} title={invoice.cliente?.razonSocial || ''}>{invoice.cliente?.razonSocial}</td>
                        <td style={tdStyle}>
                          {new Date(invoice.fechaComprobante || invoice.fecha).toLocaleDateString('es-AR')}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: '600' }}>
                          ${invoice.total?.toFixed(2)}
                        </td>
                        <td style={tdStyle} title={invoice.cae || ''}>
                          {invoice.cae || 'N/A'}
                        </td>
                        <td style={tdStyle}>
                          {/* Columna de relación con NC */}
                          {([3, 8, 13, 53].includes(Number(invoice.tipoComprobante)) && invoice.notaCreditoDe) ? (
                            // Es una NC; mostrar a qué factura pertenece
                            (() => {
                              const original = invoices.find(i => i._id === invoice.notaCreditoDe);
                              if (original) {
                                return (
                                  <span title="Nota de Crédito de">
                                    De {String(original.puntoVenta).padStart(5, '0')}-{String(original.numero).padStart(8, '0')}
                                  </span>
                                );
                              }
                              return <span>De —</span>;
                            })()
                          ) : (
                            // Es una factura; si fue anulada y tiene NC, mostrar PV-NUM de la NC
                            (invoice.notaCreditoId ? (() => {
                              const nc = invoices.find(i => i._id === invoice.notaCreditoId);
                              if (nc) {
                                return (
                                  <span title="Anulada por Nota de Crédito">
                                    NC {String(nc.puntoVenta).padStart(5, '0')}-{String(nc.numero).padStart(8, '0')}
                                  </span>
                                );
                              }
                              return <span>NC —</span>;
                            })() : <span>—</span>)
                          )}
                        </td>
                        <td style={tdStyle}>
                          <span style={statusBadgeStyle(invoice.estado)}>
                            {invoice.estado === 'autorizada' ? '✅ Autorizada'
                              : invoice.estado === 'anulada' ? '❌ Anulada'
                                : '⏳ Pendiente'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <div style={actionsContainerStyle}>
                            <button
                              onClick={() => handleViewInvoice(invoice)}
                              style={{ ...buttonStyle('secondary'), padding: '6px 12px' }}
                            >
                              👁️ Ver
                            </button>
                            {invoice.pdfUrl && (
                              <a
                                href={`${invoice.pdfUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ ...buttonStyle('primary'), padding: '6px 12px', textDecoration: 'none' }}
                              >
                                📄 PDF
                              </a>
                            )}
                            {invoice.estado === 'autorizada' && ![3, 8, 13, 53].includes(Number(invoice.tipoComprobante)) && (
                              <button
                                onClick={() => handleAnular(invoice._id)}
                                style={{ ...buttonStyle('danger'), padding: '6px 12px' }}
                              >
                                ❌ Anular
                              </button>
                            )}
                            {/* Botón PDF NC removido para ahorrar espacio; se puede descargar desde el modal de la NC */}
                            {invoice.estado === 'anulada' && invoice.notaCreditoId && (
                              <button
                                onClick={() => handleViewNC(invoice)}
                                style={{ ...buttonStyle('primary'), padding: '6px 12px' }}
                              >
                                🔎 Ver NC
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Modal: Nueva Factura */}
        {showModal && (
          <div style={modalOverlayStyle} onClick={() => setShowModal(false)}>
            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
                ➕ Nueva Factura Manual
              </h3>

              <form onSubmit={handleSubmit}>
                {/* Datos del comprobante */}
                <div className="form-grid" style={{ gap: '15px', marginBottom: '20px' }}>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>Punto de Venta</label>
                    <select
                      name="puntoVenta"
                      value={formData.puntoVenta}
                      onChange={handleInputChange}
                      style={selectStyle}
                      required
                    >
                      {fiscalStatus?.puntosVenta?.map(pv => (
                        <option key={pv.numero} value={pv.numero}>
                          {String(pv.numero).padStart(5, '0')} - {pv.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={formGroupStyle}>
                    <label style={labelStyle}>Tipo de Comprobante</label>
                    <select
                      name="tipoComprobante"
                      value={formData.tipoComprobante}
                      onChange={handleInputChange}
                      style={selectStyle}
                      required
                    >
                      {Object.entries(tiposComprobante).map(([key, value]) => (
                        <option key={key} value={key}>{value}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Datos del cliente */}
                <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', marginTop: '20px' }}>
                  📝 Datos del Cliente
                </h4>

                <div className="form-grid" style={{ gap: '15px' }}>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>Condición IVA</label>
                    <select
                      name="cliente.condicionIVA"
                      value={formData.cliente.condicionIVA}
                      onChange={handleInputChange}
                      style={selectStyle}
                    >
                      {Object.entries(condicionesIVA).map(([key, value]) => (
                        <option key={key} value={key}>{value}</option>
                      ))}
                    </select>
                  </div>

                  <div style={formGroupStyle}>
                    <label style={labelStyle}>Razón Social / Nombre</label>
                    <input
                      type="text"
                      name="cliente.razonSocial"
                      value={formData.cliente.razonSocial}
                      onChange={handleInputChange}
                      style={inputStyle}
                      required
                    />
                  </div>
                </div>

                <div className="form-grid" style={{ gap: '15px' }}>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>Tipo Documento</label>
                    <select
                      name="cliente.tipoDocumento"
                      value={formData.cliente.tipoDocumento}
                      onChange={handleInputChange}
                      style={selectStyle}
                    >
                      <option value="80">CUIT</option>
                      <option value="86">CUIL</option>
                      <option value="96">DNI</option>
                      <option value="99">Sin identificar</option>
                    </select>
                  </div>

                  <div style={formGroupStyle}>
                    <label style={labelStyle}>Número</label>
                    <input
                      type="text"
                      name="cliente.numeroDocumento"
                      value={formData.cliente.numeroDocumento}
                      onChange={handleInputChange}
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Items */}
                <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', marginTop: '20px' }}>
                  🛒 Items
                </h4>

                {formData.items.map((item, index) => (
                  <div key={index} style={{
                    padding: '15px',
                    background: '#f7fafc',
                    borderRadius: '8px',
                    marginBottom: '15px'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Descripción</label>
                        <input
                          type="text"
                          value={item.descripcion}
                          onChange={(e) => handleItemChange(index, 'descripcion', e.target.value)}
                          style={inputStyle}
                          required
                        />
                      </div>

                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Cantidad</label>
                        <input
                          type="number"
                          inputMode="decimal"
                          value={item.cantidad}
                          onChange={(e) => handleItemChange(index, 'cantidad', parseFloat(e.target.value))}
                          style={inputStyle}
                          min="0.01"
                          step="0.01"
                          required
                        />
                      </div>

                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Precio Unit.</label>
                        <input
                          type="number"
                          inputMode="decimal"
                          value={item.precioUnitario}
                          onChange={(e) => handleItemChange(index, 'precioUnitario', parseFloat(e.target.value))}
                          style={inputStyle}
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>

                      <div style={formGroupStyle}>
                        <label style={labelStyle}>IVA</label>
                        <select
                          value={item.alicuotaIVA}
                          onChange={(e) => handleItemChange(index, 'alicuotaIVA', parseInt(e.target.value))}
                          style={selectStyle}
                        >
                          <option value="3">0%</option>
                          <option value="8">5%</option>
                          <option value="4">10.5%</option>
                          <option value="5">21%</option>
                          <option value="6">27%</option>
                        </select>
                      </div>

                      {formData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          style={{
                            ...buttonStyle('danger'),
                            padding: '10px',
                            marginBottom: '0'
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>

                    <div style={{ marginTop: '10px', fontSize: '14px', fontWeight: '600' }}>
                      Subtotal: ${(item.cantidad * item.precioUnitario).toFixed(2)}
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addItem}
                  style={{
                    ...buttonStyle('secondary'),
                    width: '100%',
                    marginBottom: '20px'
                  }}
                >
                  ➕ Agregar Item
                </button>

                {/* Total */}
                <div style={{
                  padding: '15px',
                  background: '#f7fafc',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  textAlign: 'right'
                }}>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>
                    Total: ${formData.items.reduce((acc, item) =>
                      acc + (item.cantidad * item.precioUnitario), 0
                    ).toFixed(2)}
                  </div>
                </div>

                {/* Botones */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="submit"
                    style={{ ...buttonStyle('primary'), flex: 1 }}
                    disabled={loading}
                  >
                    {loading ? '⏳ Generando...' : '💾 Generar Factura'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{ ...buttonStyle('secondary'), flex: 1 }}
                  >
                    ❌ Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal: Ver Detalle */}
        {selectedInvoice && (
          <div style={modalOverlayStyle} onClick={() => setSelectedInvoice(null)}>
            <div style={{ ...modalContentStyle, maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
                📄 Detalle de Factura
              </h3>

              <div style={{ marginBottom: '20px' }}>
                <strong>Comprobante:</strong> {String(selectedInvoice.puntoVenta).padStart(5, '0')}-
                {String(selectedInvoice.numero).padStart(8, '0')}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <strong>Tipo:</strong> {tiposComprobante[selectedInvoice.tipoComprobante]}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <strong>Cliente:</strong> {selectedInvoice.cliente?.razonSocial}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <strong>CAE:</strong> {selectedInvoice.cae}
                <br />
                <strong>Vencimiento CAE:</strong> {new Date(selectedInvoice.caeVencimiento).toLocaleDateString('es-AR')}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <strong>Items:</strong>
                <div className="responsive-table" style={{ marginTop: '10px' }}>
                  <table style={{ ...tableStyle, marginTop: 0 }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Descripción</th>
                        <th style={thStyle}>Cant.</th>
                        <th style={thStyle}>Precio</th>
                        <th style={thStyle}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items?.map((item, idx) => (
                        <tr key={idx}>
                          <td style={tdStyle}>{item.descripcion}</td>
                          <td style={tdStyle}>{item.cantidad}</td>
                          <td style={tdStyle}>${item.precioUnitario.toFixed(2)}</td>
                          <td style={tdStyle}>${(item.cantidad * item.precioUnitario).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{
                padding: '15px',
                background: '#f7fafc',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div><strong>Subtotal:</strong> ${selectedInvoice.subtotal?.toFixed(2)}</div>
                <div><strong>IVA:</strong> ${selectedInvoice.importeIVA?.toFixed(2)}</div>
                <div style={{ fontSize: '18px', fontWeight: '700', marginTop: '10px' }}>
                  <strong>TOTAL:</strong> ${selectedInvoice.total?.toFixed(2)}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleOpenPdf(selectedInvoice, 'ticket80')}
                  style={{ ...buttonStyle('secondary'), flex: 1 }}
                  title="Abrir ticket fiscal 80mm"
                >
                  🧾 Ticket 80mm
                </button>
                <button
                  onClick={() => handleOpenPdf(selectedInvoice, 'a4')}
                  style={{ ...buttonStyle('primary'), flex: 1 }}
                  title="Abrir factura A4"
                >
                  📄 Factura A4
                </button>
                {/* Si es una NC y tiene vínculo a la factura original, mostrar acceso */}
                {[3, 8, 13, 53].includes(Number(selectedInvoice.tipoComprobante)) && selectedInvoice.notaCreditoDe && (
                  <button
                    onClick={() => handleViewOriginalFromNC(selectedInvoice)}
                    style={{ ...buttonStyle('secondary'), flex: 1 }}
                  >
                    ↩️ Ver Factura Original
                  </button>
                )}
                <button
                  onClick={() => setSelectedInvoice(null)}
                  style={{ ...buttonStyle('secondary'), flex: 1 }}
                >
                  ✅ Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoices;
